// One context-menu host for the whole app: right-click anywhere registers
// items through openMenu(); grouped, shortcut-annotated, keyboard-navigable.

import { useEffect, useRef } from "react";
import { create } from "zustand";
import { AnimatePresence, motion } from "framer-motion";
import { springUi, durations, easeIn } from "@/motion/springs";

export interface MenuItem {
  label: string;
  shortcut?: string;
  danger?: boolean;
  disabled?: boolean;
  onSelect?: () => void;
  separator?: boolean;
}

interface MenuState {
  items: MenuItem[];
  x: number;
  y: number;
  open: boolean;
  openAt: (x: number, y: number, items: MenuItem[]) => void;
  close: () => void;
}

export const useMenu = create<MenuState>((set) => ({
  items: [],
  x: 0,
  y: 0,
  open: false,
  openAt: (x, y, items) => set({ x, y, items, open: true }),
  close: () => set({ open: false }),
}));

export function openMenu(e: { clientX: number; clientY: number; preventDefault(): void }, items: MenuItem[]): void {
  e.preventDefault();
  useMenu.getState().openAt(e.clientX, e.clientY, items);
}

export function ContextMenuHost() {
  const { open, x, y, items, close } = useMenu();
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (!ref.current?.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") close();
      if (e.key === "ArrowDown" || e.key === "ArrowUp") {
        const buttons = ref.current?.querySelectorAll<HTMLButtonElement>("button:not(:disabled)");
        if (!buttons || buttons.length === 0) return;
        const idx = Array.from(buttons).indexOf(document.activeElement as HTMLButtonElement);
        const next = e.key === "ArrowDown" ? (idx + 1) % buttons.length : (idx - 1 + buttons.length) % buttons.length;
        buttons[next].focus();
        e.preventDefault();
      }
    };
    document.addEventListener("pointerdown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("pointerdown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open, close]);

  const left = Math.min(x, window.innerWidth - 220);
  const top = Math.min(y, window.innerHeight - Math.min(items.length * 36 + 16, 400));

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          ref={ref}
          className="bd-menu"
          style={{ left, top }}
          role="menu"
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1, transition: springUi }}
          exit={{ opacity: 0, transition: { duration: durations.fast, ease: easeIn } }}
        >
          {items.map((item, i) =>
            item.separator ? (
              <div key={i} className="bd-menu__sep" role="separator" />
            ) : (
              <button
                key={i}
                role="menuitem"
                className={`bd-menu__item${item.danger ? " bd-menu__item--danger" : ""}`}
                disabled={item.disabled}
                onClick={() => {
                  close();
                  item.onSelect?.();
                }}
              >
                {item.label}
                {item.shortcut && <span className="bd-menu__kbd">{item.shortcut}</span>}
              </button>
            ),
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}
